import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Search, Key, ArrowDownUp } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  ToolCard,
  ToolFormDialog,
  StockAdjustDialog,
  AddToRentalDialog,
  RentalEquipmentFormDialog
} from '@/components/tools';

const Tools = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rentalDialogOpen, setRentalDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [addToRentalDialogOpen, setAddToRentalDialogOpen] = useState(false);
  
  // Selected items
  const [deleteToolId, setDeleteToolId] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [toolToAddToRental, setToolToAddToRental] = useState(null);
  
  // Form states
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [stockAdjustment, setStockAdjustment] = useState('');
  const [rentalQuantity, setRentalQuantity] = useState(1);
  const [rentalStorageLocation, setRentalStorageLocation] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    article_number: '',
    order_number: '',
    supplier: '',
    photo: '',
    quantity_total: 1,
    expiration_date: '',
    location: '',
    barcode: '',
  });
  
  const [rentalFormData, setRentalFormData] = useState({
    name: '',
    type: '',
    article_number: '',
    order_number: '',
    supplier: '',
    description: '',
    photo: '',
    quantity_total: 1,
    storage_location: '',
    expiration_date: '',
  });

  // Effects
  useEffect(() => {
    loadTools();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = tools.filter((tool) =>
      tool.name.toLowerCase().includes(term) ||
      tool.type.toLowerCase().includes(term) ||
      tool.article_number.toLowerCase().includes(term) ||
      (tool.barcode && tool.barcode.toLowerCase().includes(term)) ||
      (tool.supplier && tool.supplier.toLowerCase().includes(term))
    );

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });
      }
      if (sortBy === 'supplier') {
        const sa = (a.supplier || '').toLowerCase();
        const sb = (b.supplier || '').toLowerCase();
        if (!sa && sb) return 1;
        if (sa && !sb) return -1;
        return sa.localeCompare(sb, 'fr', { sensitivity: 'base' });
      }
      if (sortBy === 'expiration') {
        // Soonest expiring first; tools without expiration_date go to the end
        const ea = a.expiration_date || '';
        const eb = b.expiration_date || '';
        if (!ea && eb) return 1;
        if (ea && !eb) return -1;
        if (!ea && !eb) return 0;
        return ea.localeCompare(eb);
      }
      // 'recent' (default): newest first by created_at, fallback to id
      const da = a.created_at || '';
      const db = b.created_at || '';
      if (da && db) return db.localeCompare(da);
      return (b.id || '').localeCompare(a.id || '');
    });

    setFilteredTools(sorted);
  }, [searchTerm, sortBy, tools]);

  // API calls
  const loadTools = async () => {
    try {
      const { data } = await axios.get(`${API}/tools`);
      setTools(data);
      setFilteredTools(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des outils');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(`${API}/tools/upload-photo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData({ ...formData, photo: data.url });
      toast.success('Photo téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRentalPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(`${API}/rentals/upload-photo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRentalFormData({ ...rentalFormData, photo: data.url });
      toast.success('Photo téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/tools/${editingId}`, formData);
        toast.success('Outil mis à jour');
      } else {
        await axios.post(`${API}/tools`, formData);
        toast.success('Outil créé avec succès');
      }
      setDialogOpen(false);
      resetForm();
      loadTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleRentalSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/rentals/equipment`, rentalFormData);
      toast.success('Équipement de location créé avec succès');
      setRentalDialogOpen(false);
      resetRentalForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleDelete = (toolId) => {
    setDeleteToolId(toolId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteToolId) return;
    try {
      await axios.delete(`${API}/tools/${deleteToolId}`);
      toast.success('Outil supprimé avec succès');
      loadTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteToolId(null);
    }
  };

  const handleStockAdjust = async (adjustment) => {
    if (!selectedTool) return;
    try {
      await axios.post(`${API}/tools/${selectedTool.id}/adjust-stock`, {
        quantity_change: adjustment,
      });
      toast.success('Stock ajusté avec succès');
      setAdjustDialogOpen(false);
      setStockAdjustment('');
      setSelectedTool(null);
      loadTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajustement');
    }
  };

  const handleAddToRental = async (e) => {
    e.preventDefault();
    if (!toolToAddToRental) return;
    
    try {
      const equipmentData = {
        name: toolToAddToRental.name,
        type: toolToAddToRental.type || null,
        article_number: toolToAddToRental.article_number || null,
        serial_number: toolToAddToRental.serial_number || null,
        order_number: toolToAddToRental.order_number || null,
        supplier: toolToAddToRental.supplier || null,
        photo: toolToAddToRental.photo || null,
        quantity_total: rentalQuantity,
        storage_location: rentalStorageLocation || toolToAddToRental.location || null,
        expiration_date: toolToAddToRental.expiration_date || null,
      };
      
      await axios.post(`${API}/rentals/equipment`, equipmentData);
      await axios.put(`${API}/tools/${toolToAddToRental.id}/mark-rental`, { is_in_rental: true });
      
      toast.success('Équipement ajouté à la location avec succès');
      setAddToRentalDialogOpen(false);
      setToolToAddToRental(null);
      setRentalQuantity(1);
      setRentalStorageLocation('');
      loadTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout à la location');
    }
  };

  // Form helpers
  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      article_number: '',
      order_number: '',
      supplier: '',
      photo: '',
      quantity_total: 1,
      expiration_date: '',
      location: '',
      barcode: '',
    });
    setEditingId(null);
  };

  const resetRentalForm = () => {
    setRentalFormData({
      name: '',
      type: '',
      article_number: '',
      order_number: '',
      supplier: '',
      description: '',
      photo: '',
      quantity_total: 1,
      storage_location: '',
      expiration_date: '',
    });
  };

  const copyTool = (tool, e) => {
    e.stopPropagation();
    setFormData({
      name: tool.name,
      type: tool.type || '',
      article_number: '',
      order_number: tool.order_number || '',
      supplier: tool.supplier || '',
      photo: tool.photo || '',
      quantity_total: 1,
      expiration_date: tool.expiration_date || '',
      location: tool.location || '',
      barcode: '',
    });
    setEditingId(null);
    setDialogOpen(true);
    toast.info('Outil copié - Veuillez saisir un nouveau numéro d\'article');
  };

  const openEditDialog = (tool) => {
    setFormData({
      name: tool.name,
      type: tool.type,
      article_number: tool.article_number,
      order_number: tool.order_number || '',
      supplier: tool.supplier || '',
      photo: tool.photo || '',
      quantity_total: tool.quantity_total,
      expiration_date: tool.expiration_date || '',
      location: tool.location || '',
      barcode: tool.barcode || '',
    });
    setEditingId(tool.id);
    setDialogOpen(true);
  };

  const openAdjustDialog = (tool, e) => {
    e.stopPropagation();
    setSelectedTool(tool);
    setAdjustDialogOpen(true);
  };

  const openAddToRentalDialog = (tool, e) => {
    e.stopPropagation();
    setToolToAddToRental(tool);
    setRentalStorageLocation(tool.location || '');
    setAddToRentalDialogOpen(true);
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    const twoMonths = new Date();
    twoMonths.setMonth(twoMonths.getMonth() + 2);
    return date > now && date <= twoMonths;
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-heading font-bold">Outils</h1>
        </div>
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-heading font-bold">Outils</h1>
        {user?.role === 'admin' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="add-tool-button" className="rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="add-tool-menu-item"
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvel Outil
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="add-rental-menu-item"
                onClick={() => {
                  resetRentalForm();
                  setRentalDialogOpen(true);
                }}
              >
                <Key className="w-4 h-4 mr-2" />
                Matériel en Location
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Tool Form Dialog */}
      <ToolFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
        formData={formData}
        setFormData={setFormData}
        editingId={editingId}
        onSubmit={handleSubmit}
        onPhotoUpload={handlePhotoUpload}
        uploadingPhoto={uploadingPhoto}
      />

      {/* Rental Equipment Form Dialog */}
      <RentalEquipmentFormDialog
        open={rentalDialogOpen}
        onOpenChange={(open) => {
          setRentalDialogOpen(open);
          if (!open) resetRentalForm();
        }}
        formData={rentalFormData}
        setFormData={setRentalFormData}
        onSubmit={handleRentalSubmit}
        onPhotoUpload={handleRentalPhotoUpload}
        uploadingPhoto={uploadingPhoto}
      />

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="tool-search-input"
            placeholder="Rechercher (nom, type, n° article, fournisseur, code-barres)..."
            className="pl-10 rounded-lg w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <ArrowDownUp className="w-4 h-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="tool-sort-select" className="rounded-lg w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent" data-testid="sort-recent">Ajout récent</SelectItem>
              <SelectItem value="name" data-testid="sort-name">Nom (A → Z)</SelectItem>
              <SelectItem value="supplier" data-testid="sort-supplier">Fournisseur</SelectItem>
              <SelectItem value="expiration" data-testid="sort-expiration">Date d'expiration</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tool Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredTools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            user={user}
            onAdjustStock={openAdjustDialog}
            onCopy={copyTool}
            onAddToRental={openAddToRentalDialog}
            onEdit={openEditDialog}
            onDelete={handleDelete}
            formatDate={formatDate}
            isExpired={isExpired}
            isExpiringSoon={isExpiringSoon}
          />
        ))}
      </div>

      {filteredTools.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Aucun outil trouvé</p>
        </div>
      )}

      {/* Stock Adjust Dialog */}
      <StockAdjustDialog
        open={adjustDialogOpen}
        onOpenChange={(open) => {
          setAdjustDialogOpen(open);
          if (!open) {
            setStockAdjustment('');
            setSelectedTool(null);
          }
        }}
        selectedTool={selectedTool}
        stockAdjustment={stockAdjustment}
        setStockAdjustment={setStockAdjustment}
        onAdjust={handleStockAdjust}
      />

      {/* Add to Rental Dialog */}
      <AddToRentalDialog
        open={addToRentalDialogOpen}
        onOpenChange={(open) => {
          setAddToRentalDialogOpen(open);
          if (!open) {
            setToolToAddToRental(null);
            setRentalQuantity(1);
            setRentalStorageLocation('');
          }
        }}
        toolToAddToRental={toolToAddToRental}
        rentalQuantity={rentalQuantity}
        setRentalQuantity={setRentalQuantity}
        rentalStorageLocation={rentalStorageLocation}
        setRentalStorageLocation={setRentalStorageLocation}
        onSubmit={handleAddToRental}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmDelete}
        title="Supprimer l'outil"
        description="Êtes-vous sûr de vouloir supprimer cet outil ? Cette action est irréversible. Si des outils sont actuellement affectés, ils seront automatiquement retirés."
      />
    </div>
  );
};

export default Tools;
